import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import { staffActivityKeys } from './useStaffActivity';
import { staffKeys } from './useStaff';
import type {
  LocationStaffTokenConfigInput,
  ManualGrantInput,
  ManualAdjustmentInput,
  CompanyStaffTokenConfigInput,
  ApproverCredential,
} from '../types';
import {
  getLocationStaffTokenConfig,
  getStaffTokenBalances,
  getStaffTokenBalanceByStaffId,
  getStaffTokenLedger,
  getStaffTokenLedgerByStaffId,
  getPendingTokenTransfers,
  approveTokenTransfer,
  rejectTokenTransfer,
  upsertLocationStaffTokenConfig,
  settleTokenDebt,
  getCompanyTokenSummary,
  grantStaffTokensManually,
  adjustStaffTokensManually,
  runWeeklyTokenGrants,
  getCompanyStaffTokenConfig,
  updateCompanyStaffTokenConfig,
  voidTokenLedgerEntry,
  updateStaff,
  type TokenDebtSettlementInput,
  type StaffTokenControlsInput,
} from '../services/staffService';


export const staffTokenKeys = {
  all: ['staff-tokens'] as const,
  config: (locationId: string | null) => [...staffTokenKeys.all, 'config', locationId] as const,
  balances: (locationId: string | null) => [...staffTokenKeys.all, 'balances', locationId] as const,
  // Per-staff balance/ledger. The location-scoped key is a PREFIX of the
  // all-locations variant, so invalidating ``balanceByStaff(staffId)``
  // refreshes both the location-filtered read (TokenDebtPanel) and the
  // cross-location read used by the person profile.
  balanceByStaff: (staffId: string | null) => [...staffTokenKeys.all, 'balance-staff', staffId] as const,
  balanceByStaffAllLocations: (staffId: string | null) => [...staffTokenKeys.all, 'balance-staff', staffId, 'all-locations'] as const,
  ledger: (locationId: string | null) => [...staffTokenKeys.all, 'ledger', locationId] as const,
  pendingTransfers: (locationId: string | null) => [...staffTokenKeys.all, 'pending-transfers', locationId] as const,
  ledgerByStaff: (staffId: string | null) => [...staffTokenKeys.all, 'ledger-staff', staffId] as const,
  ledgerByStaffAllLocations: (staffId: string | null) => [...staffTokenKeys.all, 'ledger-staff', staffId, 'all-locations'] as const,
  companySummary: () => [...staffTokenKeys.all, 'company-summary'] as const,
  companyConfig: () => [...staffTokenKeys.all, 'company-config'] as const,
};

export function useLocationStaffTokenConfig(locationId: string | null) {
  return useQuery({
    queryKey: staffTokenKeys.config(locationId),
    queryFn: () => getLocationStaffTokenConfig(locationId!),
    enabled: Boolean(locationId),
  });
}

export function useStaffTokenBalances(locationId: string | null) {
  return useQuery({
    queryKey: staffTokenKeys.balances(locationId),
    queryFn: () => getStaffTokenBalances(locationId!),
    enabled: Boolean(locationId),
  });
}

export function useStaffTokenLedger(locationId: string | null) {
  return useQuery({
    queryKey: staffTokenKeys.ledger(locationId),
    queryFn: () => getStaffTokenLedger(locationId!),
    enabled: Boolean(locationId),
  });
}

export function useUpsertLocationStaffTokenConfig(locationId: string | null, existingId?: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: Omit<LocationStaffTokenConfigInput, 'locationId'>) =>
      upsertLocationStaffTokenConfig({ ...input, locationId: locationId! }, existingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.config(locationId) });
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.balances(locationId) });
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.ledger(locationId) });
      notify.success(t('settings.location.staffTokens.saved', 'Staff token policy updated.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('settings.location.staffTokens.saveFailed', 'Could not save the staff token policy.'),
      });
    },
  });
}

export function useStaffTokenBalance(staffId: string | null) {
  return useQuery({
    queryKey: staffTokenKeys.balanceByStaff(staffId),
    queryFn: () => getStaffTokenBalanceByStaffId(staffId!),
    enabled: Boolean(staffId),
  });
}

export function useStaffTokenLedgerByStaff(staffId: string | null) {
  return useQuery({
    queryKey: staffTokenKeys.ledgerByStaff(staffId),
    queryFn: () => getStaffTokenLedgerByStaffId(staffId!),
    enabled: Boolean(staffId),
  });
}

export function useSettleTokenDebt(staffId: string, locationId: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    // The location settles WHICH debt: a company-scope staff holds one balance
    // per location they consume at. The caller may still override it explicitly.
    mutationFn: (input: Omit<TokenDebtSettlementInput, 'staffId'>) =>
      settleTokenDebt({ locationId, ...input, staffId }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.balanceByStaff(staffId) });
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.ledgerByStaff(staffId) });
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.balances(locationId) });
      // Refresh the unified wallet activity feed — a settle creates a
      // debt_repayment ledger row + a settled StaffExpense that must show.
      queryClient.invalidateQueries({ queryKey: staffActivityKeys.all });
      // Say so when the settlement also released the grant that grant day
      // withheld, so the balance does not jump from 0 to a full grant unexplained.
      notify.success(
        result.releasedGrantTokens > 0
          ? t(
              'staff.tokens.debtSettledGrantReleased',
              'Token debt settled. {{count}} tokens granted.',
              { count: result.releasedGrantTokens },
            )
          : t('staff.tokens.debtSettled', 'Token debt settled successfully.'),
      );
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('staff.tokens.debtSettleFailed', 'Could not settle the token debt.'),
      });
    },
  });
}

export function useCompanyTokenSummary(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: staffTokenKeys.companySummary(),
    queryFn: getCompanyTokenSummary,
    // Company-wide summary endpoint requires a company-level permission; let
    // callers gate it so a location viewer without it doesn't fire a 403.
    enabled: options?.enabled ?? true,
  });
}

/**
 * Invalidate every query keyed against this staff's tokens. All reads go
 * through ``staffTokenKeys.*``; the per-staff keys are prefixes that match
 * both the location-scoped reads (``TokenDebtPanel``) and the cross-location
 * variant used by the person profile, so a single invalidation refreshes
 * every panel — which is what the first Grant/Adjust click needs (the
 * balance card used to keep the stale number until a manual reload).
 */
function invalidateTokenQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  staffId: string,
  locationId: string | null,
) {
  queryClient.invalidateQueries({ queryKey: staffTokenKeys.balanceByStaff(staffId) });
  queryClient.invalidateQueries({ queryKey: staffTokenKeys.ledgerByStaff(staffId) });
  queryClient.invalidateQueries({ queryKey: staffTokenKeys.balances(locationId) });
  // Grants/adjustments also add a row to the unified wallet activity feed.
  queryClient.invalidateQueries({ queryKey: staffActivityKeys.all });
}

export function useGrantStaffTokensManually(staffId: string, locationId: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: Omit<ManualGrantInput, 'staffId'>) =>
      grantStaffTokensManually({ ...input, staffId }),
    onSuccess: () => {
      invalidateTokenQueries(queryClient, staffId, locationId);
      notify.success(t('staff.tokens.grantSuccess', 'Tokens granted successfully.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('staff.tokens.grantFailed', 'Could not grant tokens.'),
      });
    },
  });
}

export function useAdjustStaffTokensManually(staffId: string, locationId: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: Omit<ManualAdjustmentInput, 'staffId'>) =>
      adjustStaffTokensManually({ ...input, staffId }),
    onSuccess: () => {
      invalidateTokenQueries(queryClient, staffId, locationId);
      notify.success(t('staff.tokens.adjustSuccess', 'Token balance adjusted successfully.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('staff.tokens.adjustFailed', 'Could not adjust token balance.'),
      });
    },
  });
}

export function useVoidTokenLedgerEntry(staffId: string, locationId: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ entryId, ...credential }: { entryId: string } & ApproverCredential) =>
      voidTokenLedgerEntry(entryId, credential),
    onSuccess: () => {
      invalidateTokenQueries(queryClient, staffId, locationId);
      notify.success(t('staff.tokens.voidSuccess', 'Movement reversed.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('staff.tokens.voidFailed', 'Could not reverse the movement.'),
      });
    },
  });
}

export function useRunWeeklyGrants(locationId: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ configId, runAt }: { configId: string; runAt?: string }) =>
      runWeeklyTokenGrants(configId, runAt ? { runAt } : undefined),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.balances(locationId) });
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.ledger(locationId) });
      notify.success(
        t('staff.tokens.weeklyGrantsRan', '{{count}} staff processed.', { count: data.length }),
      );
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('staff.tokens.weeklyGrantsFailed', 'Could not run weekly grants.'),
      });
    },
  });
}


// ── Peer transfer approval (manager) ─────────────────────────────────────────

export function usePendingTokenTransfers(locationId: string | null) {
  return useQuery({
    queryKey: staffTokenKeys.pendingTransfers(locationId),
    queryFn: () => getPendingTokenTransfers(locationId!),
    enabled: Boolean(locationId),
  });
}

export function useApproveTokenTransfer(locationId: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (ledgerId: string) => approveTokenTransfer(ledgerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.pendingTransfers(locationId) });
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.balances(locationId) });
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.ledger(locationId) });
      notify.success(t('staff.tokens.transferApproved', 'Transfer approved.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('staff.tokens.transferApproveFailed', 'Could not approve the transfer.'),
      });
    },
  });
}

export function useRejectTokenTransfer(locationId: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ ledgerId, reason }: { ledgerId: string; reason?: string }) =>
      rejectTokenTransfer(ledgerId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.pendingTransfers(locationId) });
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.ledger(locationId) });
      notify.success(t('staff.tokens.transferRejected', 'Transfer rejected.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('staff.tokens.transferRejectFailed', 'Could not reject the transfer.'),
      });
    },
  });
}


// ── Company-tier staff token policy ──────────────────────────────────────────

export function useCompanyStaffTokenConfig() {
  return useQuery({
    queryKey: staffTokenKeys.companyConfig(),
    queryFn: getCompanyStaffTokenConfig,
  });
}

export function useUpdateCompanyStaffTokenConfig() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: CompanyStaffTokenConfigInput) => updateCompanyStaffTokenConfig(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffTokenKeys.companyConfig() });
      notify.success(t('settings.company.tokens.saved', 'Company token policy updated.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('settings.company.tokens.saveFailed', 'Could not save the company token policy.'),
      });
    },
  });
}

// ── Per-person token exceptions (company scope) ──────────────────────────────

/**
 * Persist the three per-person token controls that live on the `Staff` row:
 * the debt-cap override, the location blacklist and the POS balance toggle.
 *
 * Invalidates BOTH staff caches. `staffKeys.all` covers the lists and the
 * detail read; `['person-staff', personId]` is the person profile's own key
 * (owned by `PersonProfilePage`, mirrored here the same way
 * `useCreateStaffFromPerson` already does). Missing the second one would leave
 * the profile showing the previous debt cap after a successful save — the
 * operator would read a number the till is no longer using.
 */
export function useUpdateStaffTokenControls(staffId: string, personId: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: StaffTokenControlsInput) => updateStaff(staffId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      if (personId) {
        queryClient.invalidateQueries({ queryKey: ['person-staff', personId] });
      }
      notify.success(
        t('staff.tokenControls.saved', 'Token settings updated for this employee.'),
      );
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t(
          'staff.tokenControls.saveFailed',
          'Could not save the token settings.',
        ),
      });
    },
  });
}
